import {create} from 'zustand';
import {
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionFilters,
  AssignRolePermissionRequest,
  BulkAssignRolePermissionsRequest,
  BulkRemoveRolePermissionsRequest,
  AssignUserPermissionRequest,
  DenyUserPermissionRequest,
  UserEffectivePermissions
} from '../models/permission.model';
import {PermissionRepository} from '../repository/PermissionRepository';
import {PermissionRepositoryImpl} from '../repository/PermissionRepositoryImpl';
import {RoleRepository} from '../repository/RoleRepository';
import {RoleRepositoryImpl} from '../repository/RoleRepositoryImpl';

interface PermissionState {
  // Data
  permissions: Permission[];
  rolePermissions: Record<number, Permission[]>;
  userEffectivePermissions: Record<number, UserEffectivePermissions>;
  pagination: any;

  // Loading states
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  assigning: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;
  assignError: string | null;

  // Filters
  filters: PermissionFilters;

  // Actions
  fetchPermissions: (filters?: PermissionFilters) => Promise<void>;
  fetchAllPermissions: () => Promise<{ permissions: Permission[]; pagination: any }>;
  createPermission: (permissionData: CreatePermissionRequest) => Promise<Permission>;
  updatePermission: (permissionData: UpdatePermissionRequest) => Promise<Permission>;
  deletePermission: (permissionId: number) => Promise<void>;

  // Role Permission Actions
  assignRolePermission: (assignmentData: AssignRolePermissionRequest) => Promise<any>;
  bulkAssignRolePermissions: (assignmentData: BulkAssignRolePermissionsRequest) => Promise<any>;
  getRolePermissions: (roleId: number) => Promise<Permission[]>;
  removeRolePermission: (roleId: number, permissionId: number) => Promise<void>;
  bulkRemoveRolePermissions: (assignmentData: BulkRemoveRolePermissionsRequest) => Promise<void>;

  // User Permission Actions
  assignUserPermission: (assignmentData: AssignUserPermissionRequest) => Promise<any>;
  denyUserPermission: (denialData: DenyUserPermissionRequest) => Promise<any>;
  getUserEffectivePermissions: (userId: number) => Promise<UserEffectivePermissions>;

  // Utility actions
  setFilters: (filters: Partial<PermissionFilters>) => void;
  clearError: () => void;
  clearCreateError: () => void;
  clearUpdateError: () => void;
  clearDeleteError: () => void;
  clearAssignError: () => void;
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  (set, get) => {
      const permissionRepository = new PermissionRepositoryImpl();
      const roleRepository = new RoleRepositoryImpl();

      return {
        // Initial state
        permissions: [],
        rolePermissions: {},
        userEffectivePermissions: {},
        pagination: null,

        loading: false,
        creating: false,
        updating: false,
        deleting: false,
        assigning: false,

        error: null,
        createError: null,
        updateError: null,
        deleteError: null,
        assignError: null,

        filters: {
          page: 1,
          limit: 10
        },

        // Permission CRUD Actions
        fetchPermissions: async (filters?: PermissionFilters) => {
          set({loading: true, error: null});

          try {
            const result = await permissionRepository.getPermissions(filters);

            set({
              permissions: result.permissions || [],
              pagination: result.pagination || {},
              loading: false
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch permissions';
            set({error: message, loading: false});
            throw error;
          }
        },

        // Fetch all permissions with pagination - NO CACHING
        fetchAllPermissions: async () => {
          set({loading: true, error: null});

          try {
            const result = await permissionRepository.getAllPermissions();

            set({
              permissions: result.permissions || [],
              pagination: result.pagination || {},
              loading: false
            });

            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch all permissions';
            set({error: message, loading: false});
            throw error;
          }
        },

        createPermission: async (permissionData: CreatePermissionRequest) => {
          set({creating: true, createError: null});

          try {
            const newPermission = await permissionRepository.createPermission(permissionData);

            set((state) => ({
              permissions: [newPermission, ...state.permissions],
              creating: false
            }));

            return newPermission;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create permission';
            set({createError: message, creating: false});
            throw error;
          }
        },

        updatePermission: async (permissionData: UpdatePermissionRequest) => {
          set({updating: true, updateError: null});

          try {
            const updatedPermission = await permissionRepository.updatePermission(permissionData.id, {
              description: permissionData.description
            });

            set((state) => ({
              permissions: state.permissions.map(permission =>
                permission.id === updatedPermission.id ? updatedPermission : permission
              ),
              updating: false
            }));

            return updatedPermission;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update permission';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        deletePermission: async (permissionId: number) => {
          set({deleting: true, deleteError: null});

          try {
            await permissionRepository.deletePermission(permissionId);

            set((state) => ({
              permissions: state.permissions.filter(permission => permission.id !== permissionId),
              deleting: false
            }));
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to delete permission';
            set({deleteError: message, deleting: false});
            throw error;
          }
        },

        // Role Permission Actions
        assignRolePermission: async (assignmentData: AssignRolePermissionRequest) => {
          set({assigning: true, assignError: null});

          try {
            const result = await roleRepository.assignRolePermission(assignmentData);

            // Refresh role permissions cache
            const rolePermissions = await roleRepository.getRolePermissions(assignmentData.roleId);
            set((state) => ({
              rolePermissions: {
                ...state.rolePermissions,
                [assignmentData.roleId]: rolePermissions
              },
              assigning: false
            }));

            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to assign permission to role';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        bulkAssignRolePermissions: async (assignmentData: BulkAssignRolePermissionsRequest) => {
          set({assigning: true, assignError: null});

          try {
            const result = await roleRepository.bulkAssignRolePermissions(assignmentData);

            // Refresh role permissions cache
            const rolePermissions = await roleRepository.getRolePermissions(assignmentData.roleId);
            set((state) => ({
              rolePermissions: {
                ...state.rolePermissions,
                [assignmentData.roleId]: rolePermissions
              },
              assigning: false
            }));

            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to bulk assign permissions to role';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        getRolePermissions: async (roleId: number) => {
          try {
            const permissions = await roleRepository.getRolePermissions(roleId);

            set((state) => ({
              rolePermissions: {
                ...state.rolePermissions,
                [roleId]: permissions
              }
            }));

            return permissions;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch role permissions';
            set({error: message});
            throw error;
          }
        },

        removeRolePermission: async (roleId: number, permissionId: number) => {
          set({assigning: true, assignError: null});

          try {
            await roleRepository.removeRolePermission(roleId, permissionId);

            // Refresh role permissions cache
            const rolePermissions = await roleRepository.getRolePermissions(roleId);
            set((state) => ({
              rolePermissions: {
                ...state.rolePermissions,
                [roleId]: rolePermissions
              },
              assigning: false
            }));
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to remove permission from role';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        bulkRemoveRolePermissions: async (assignmentData: BulkRemoveRolePermissionsRequest) => {
          set({assigning: true, assignError: null});

          try {
            await roleRepository.bulkRemoveRolePermissions(
              assignmentData.roleId,
              assignmentData.permissionIds,
              assignmentData.reason
            );

            // Refresh role permissions cache
            const rolePermissions = await roleRepository.getRolePermissions(assignmentData.roleId);
            set((state) => ({
              rolePermissions: {
                ...state.rolePermissions,
                [assignmentData.roleId]: rolePermissions
              },
              assigning: false
            }));
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to bulk remove permissions from role';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        // User Permission Actions
        assignUserPermission: async (assignmentData: AssignUserPermissionRequest) => {
          set({assigning: true, assignError: null});

          try {
            const result = await permissionRepository.assignUserPermission(assignmentData);

            // Refresh user effective permissions cache
            const effectivePermissions = await permissionRepository.getUserEffectivePermissions(assignmentData.userId);
            set((state) => ({
              userEffectivePermissions: {
                ...state.userEffectivePermissions,
                [assignmentData.userId]: effectivePermissions
              },
              assigning: false
            }));

            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to assign permission to user';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        denyUserPermission: async (denialData: DenyUserPermissionRequest) => {
          set({assigning: true, assignError: null});

          try {
            const result = await permissionRepository.denyUserPermission(denialData);

            // Refresh user effective permissions cache
            const effectivePermissions = await permissionRepository.getUserEffectivePermissions(denialData.userId);
            set((state) => ({
              userEffectivePermissions: {
                ...state.userEffectivePermissions,
                [denialData.userId]: effectivePermissions
              },
              assigning: false
            }));

            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to deny permission for user';
            set({assignError: message, assigning: false});
            throw error;
          }
        },

        getUserEffectivePermissions: async (userId: number) => {
          try {
            const effectivePermissions = await permissionRepository.getUserEffectivePermissions(userId);

            set((state) => ({
              userEffectivePermissions: {
                ...state.userEffectivePermissions,
                [userId]: effectivePermissions
              }
            }));

            return effectivePermissions;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch user effective permissions';
            set({error: message});
            throw error;
          }
        },

        // Utility actions
        setFilters: (newFilters) => {
          set((state) => ({
            filters: {...state.filters, ...newFilters}
          }));
        },

        clearError: () => set({error: null}),
        clearCreateError: () => set({createError: null}),
        clearUpdateError: () => set({updateError: null}),
        clearDeleteError: () => set({deleteError: null}),
        clearAssignError: () => set({assignError: null}),

        reset: () => {
          set({
            permissions: [],
            rolePermissions: {},
            userEffectivePermissions: {},
            pagination: null,
            loading: false,
            creating: false,
            updating: false,
            deleting: false,
            assigning: false,
            error: null,
            createError: null,
            updateError: null,
            deleteError: null,
            assignError: null,
            filters: {page: 1, limit: 10}
          });
        }
      };
    }
);
